import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import { useCheckUserPermission } from "../../../../api/hooks/useCheckUserPermission";
import useColors from "../../../../api/hooks/useColors";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import useStaticIPs from "../../../../api/hooks/useStaticIPs";
import { integrationKeys, oktaKeys } from "../../../../api/queryKeys";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import CustomCopyButton from "../../../../components/CustomCopyButton";
import CustomToolTipWithPermission from "../../../../components/CustomToolTipWithPermission";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import {
  PLATFORM_USER_ACTIONS,
  VIEWER_ACCESS_DENIED_MESSAGE,
} from "../../../../utils/constants";
import { logos } from "../../environment/icons/iconExport";
import BreezeStaticIPsModal from "../components/BreezeStaticIPsModal";
import Guideline from "../components/Guideline";
import GuidelineInstruction from "../components/GuidelineInstruction";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import okta_app_name from "./images/okta_app_name.png";
import okta_client_id from "./images/okta_client_id.png";
import okta_create_app from "./images/okta_create_app.png";
import okta_create_app_menu from "./images/okta_create_app_menu.png";
import okta_diable_dpop from "./images/okta_diable_dpop.png";
import okta_domain from "./images/okta_domain.png";
import okta_general_settings_1 from "./images/okta_general_settings_1.png";
import okta_general_settings_2 from "./images/okta_general_settings_2.png";
import okta_key_generation from "./images/okta_key_generation.png";
import okta_kid_copy from "./images/okta_kid_copy.png";
import okta_management_scopes from "./images/okta_management_scopes.png";
import okta_optional_error from "./images/okta_optional_error.png";
import okta_pop_up from "./images/okta_pop_up.png";
import okta_private_key from "./images/okta_private_key.png";
import okta_rate_limits from "./images/okta_rate_limits.png";
import okta_role from "./images/okta_role.png";
import okta_save_key from "./images/okta_save_key.png";
import okta_scopes_grant from "./images/okta_scopes_grant.png";

const OktaIntegration = () => {
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);

  const [loading, setLoading] = useState(false);
  const [customerDomain, setCustomerDomain] = useState("");
  const [clientId, setClientId] = useState("");
  const [kid, setKid] = useState("");
  const [privateKey, setPrivateKey] = useState("");
  const [optionalExpanded, setOptionalExpanded] = useState(false);
  const [showStaticIPsModal, setShowStaticIPsModal] = useState(false);

  const { staticIPs: allowlistIps } = useStaticIPs();
  const {
    registerOktaIntegration,
    getOktaReadScopes,
    checkOktaReadScopes,
    getOktaManagementScopes,
    trackEvent,
    checkOktaManagementScopes,
  } = useAPI();
  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const queryClient = useQueryClient();
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("okta");
  const { permitted: hasPermission } = useCheckUserPermission(
    PLATFORM_USER_ACTIONS.MODIFY_INTEGRATION
  );
  const colors = useColors();

  const enabledFetching = isIntegrated && !loading && hasPermission;

  const { data: managementScopes, isLoading: isLoadingManagementScopes } =
    useQuery({
      queryKey: oktaKeys.managementScopes(),
      queryFn: async () => {
        const response = await getOktaManagementScopes();
        return response.data;
      },
    });

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);

  const { data: readScopes } = useQuery({
    queryKey: oktaKeys.readScopes(),
    queryFn: getOktaReadScopes,
    select: (data) => data.data,
  });

  const { data: missingScopes, isFetching: isLoadingCheckOktaReadScopes } =
    useQuery({
      queryKey: oktaKeys.scopeCheck(),
      queryFn: async () => {
        const response = await checkOktaReadScopes();
        return response;
      },
      select: (data) => data.data,

      enabled: enabledFetching,
    });
  const {
    data: missingManagementScopes,
    isLoading: isLoadingCheckOktaManagementScopes,
  } = useQuery({
    queryKey: oktaKeys.manualMgmtScopeCheck(),
    queryFn: async () => {
      const response = await checkOktaManagementScopes();
      return response;
    },
    select: (data) => data.data,

    enabled: enabledFetching,
  });

  useEffect(() => {
    if (isIntegrated && !loading) {
      queryClient.invalidateQueries({
        queryKey: oktaKeys.scopeCheck(),
      });
    }
  }, [isIntegrated, loading]);

  const onSubmit = async () => {
    setLoading(true);
    try {
      await registerOktaIntegration(customerDomain, clientId, kid, privateKey);
      queryClient.invalidateQueries({
        queryKey: integrationKeys.all,
      });
      Toast.success("Okta integration successful");
      queryClient.invalidateQueries({
        queryKey: oktaKeys.scopeCheck(),
      });
    } catch (error) {
      if (error.response) {
        Toast.error(
          `Something went wrong: ${error.response.data.detail}, please make sure to Grant okta.users.read scope`
        );
      } else {
        Toast.error("An error occurred while processing the request.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCheckScopes = async () => {
    queryClient.invalidateQueries({
      queryKey: oktaKeys.scopeCheck(),
    });
    trackEvent("User clicked on Check Scopes button in Okta Integration");
  };

  const handleOptionalAccordionChange = (event, isExpanded) => {
    setOptionalExpanded(isExpanded);
  };

  const handleCheckManagementScopes = async () => {
    queryClient.invalidateQueries({
      queryKey: oktaKeys.manualMgmtScopeCheck(),
    });
    trackEvent(
      "User clicked on Check Management Scopes button in Okta Integration"
    );
  };

  return (
    <div>
      <Box>
        <Guideline
          title={
            !isIntegrated
              ? "Once integrated, the Breeze platform will automatically read the configurations from Okta and detect critical gaps."
              : ""
          }
          image={logos.OktaLogo}
          name={"Okta"}
          content={
            loading || isIntegratedLoading ? (
              <LoadingIcon size={25} />
            ) : isIntegrated && !guideMode ? (
              <Box>
                <Typography variant={"h4"}>
                  Okta Identity Workforce is integrated!
                </Typography>
                {/* Inline Check Scopes Section */}
                <Box
                  mt={3}
                  mb={2}
                  p={2}
                  sx={{ background: colors.grey_100, borderRadius: 2 }}
                >
                  <Typography variant="h6" gutterBottom>
                    Check Okta Scopes
                  </Typography>
                  <CustomToolTipWithPermission
                    title={!hasPermission ? VIEWER_ACCESS_DENIED_MESSAGE : ""}
                    permission={hasPermission}
                    placement="top"
                  >
                    <PrimaryButton
                      onClick={handleCheckScopes}
                      sx={{
                        minWidth: 160,
                        cursor: hasPermission ? "pointer" : "default",
                      }}
                    >
                      {isLoadingCheckOktaReadScopes ? (
                        <LoadingIcon
                          size={24}
                          sx={{ color: colors.common_white }}
                        />
                      ) : (
                        "Check Scopes"
                      )}
                    </PrimaryButton>
                  </CustomToolTipWithPermission>
                  {isLoadingCheckOktaReadScopes && (
                    <Box mt={2}>
                      <Typography variant="body2">
                        Checking scopes...
                      </Typography>
                    </Box>
                  )}
                  {missingScopes?.length > 0 && (
                    <Box mt={2}>
                      <Typography color="error" variant="subtitle1">
                        Please grant the missing scopes to the application:
                      </Typography>
                      {missingScopes.map((scope, index) => (
                        <Typography key={index} color="error">
                          <b>
                            {index + 1}. {scope}
                          </b>
                        </Typography>
                      ))}
                    </Box>
                  )}
                  {!hasPermission && (
                    <Box mt={2}>
                      <Typography color="error" variant="subtitle1">
                        Your role, "Viewer", does not have permission to perform
                        this action.
                      </Typography>
                    </Box>
                  )}
                  {missingScopes?.length === 0 &&
                    !isLoadingCheckOktaReadScopes &&
                    hasPermission && (
                      <Box mt={2}>
                        <Typography color="success.main" variant="subtitle1">
                          All required scopes are granted!
                        </Typography>
                      </Box>
                    )}
                </Box>
              </Box>
            ) : (
              // TODO hardcoded to be same as screenshot accordion
              <Box width={"740px"} mr="10px">
                {/* BUTTONS TO CONTROL EXPANSION */}
                <Box mb="20px">
                  <Grid container spacing={2} direction={"row"}>
                    {guideMode && <IntegrationGuideAlert />}

                    <Grid item>
                      <SecondaryButton onClick={handleExpandAll}>
                        Expand All Screenshots
                      </SecondaryButton>
                    </Grid>
                    <Grid item>
                      <SecondaryButton onClick={handleCollapseAll}>
                        Collapse All Screenshots
                      </SecondaryButton>
                    </Grid>
                    <Grid item>
                      <SecondaryButton
                        onClick={() => setShowStaticIPsModal(true)}
                      >
                        View Breeze Static IPs
                      </SecondaryButton>
                    </Grid>
                  </Grid>
                </Box>

                {/* SETUP INSTRUCTIONS */}
                <form onSubmit={onSubmit}>
                  <GuidelineStepByStep>
                    <GuidelineStep title="Log in to Okta Workforce Identity console">
                      <GuidelineInstruction text="Make sure you have a “Super Administrator” role - required for the next steps." />
                    </GuidelineStep>
                    <GuidelineStep title="Create a new application in Okta with the following settings">
                      <GuidelineInstruction text="Select API Service as the Sign-in method." />
                      <ScreenshotAccordion
                        images={[
                          {
                            file: okta_create_app,
                            altext: "Create a new app integration",
                          },
                          {
                            file: okta_create_app_menu,
                            altext: "Create a new app integration",
                          },
                        ]}
                        onExpanded={addToExpandedList}
                      />
                      <GuidelineInstruction text="Name the new App as: “Breeze Platform Integration“." />
                      <ScreenshotAccordion
                        images={[
                          {
                            file: okta_app_name,
                            altext: "Name the app",
                          },
                        ]}
                        onExpanded={addToExpandedList}
                      />
                      <GuidelineInstruction text="Click “Save” to save the new application." />
                    </GuidelineStep>
                    <GuidelineStep title="Change the authentication method to Public key / Private key">
                      <GuidelineInstruction text="Click “Edit” and change the Client authentication to use “Public key / Private key“." />
                      <GuidelineInstruction text="Keep the default setting - “Save keys in Okta” - and click “Add key”" />
                      <ScreenshotAccordion
                        images={[
                          {
                            note: "before:",
                            file: okta_general_settings_1,
                            altext: "General settings: default",
                          },
                          {
                            note: "after:",
                            file: okta_general_settings_2,
                            altext: "General settings: required",
                          },
                        ]}
                        onExpanded={addToExpandedList}
                      />
                    </GuidelineStep>
                    <GuidelineStep title="Generate a new key for authentication">
                      <ScreenshotAccordion
                        images={[
                          {
                            file: okta_key_generation,
                            altext: "Key generation dialog",
                          },
                        ]}
                        onExpanded={addToExpandedList}
                      />
                    </GuidelineStep>
                    <GuidelineStep title="Get the Private key">
                      <GuidelineInstruction text="Click “PEM“ -> “Copy to clipboard“ and paste it here" />
                      <TextField
                        required
                        fullWidth
                        variant="filled"
                        name="privateKey"
                        label="Private Key (starts with '-----BEGIN PRIVATE KEY----- ...')"
                        value={privateKey}
                        onChange={(e) => setPrivateKey(e.target.value)}
                        multiline={true}
                        autoComplete="off"
                        sx={{ maxHeight: "300px", overflow: "auto" }}
                        disabled={guideMode}
                      />
                      <ScreenshotAccordion
                        images={[
                          {
                            file: okta_private_key,
                            altext: "Okta private key example",
                            note: "NOTE: the values shown in the screenshot are for illustration purposes only",
                          },
                        ]}
                        onExpanded={addToExpandedList}
                      />

                      <GuidelineInstruction text="Once safely copied, click “Done“" />
                      <GuidelineInstruction text="Click “Save” to complete the key pair generation" />
                      <ScreenshotAccordion
                        images={[
                          {
                            file: okta_save_key,
                            altext: "Key generation dialog",
                          },
                        ]}
                        onExpanded={addToExpandedList}
                      />
                      <GuidelineInstruction text='A pop-up appears, click "Save"' />
                      <ScreenshotAccordion
                        images={[
                          {
                            file: okta_pop_up,
                            altext: "Client authentication dialog",
                          },
                        ]}
                        onExpanded={addToExpandedList}
                      />
                    </GuidelineStep>
                    <GuidelineStep title="Copy the “kid”">
                      <GuidelineInstruction text="Copy the “kid” value from the Public key section and paste it here." />
                      <TextField
                        required
                        fullWidth
                        variant="filled"
                        name="kid"
                        label="Key ID (kid)"
                        value={kid}
                        autoComplete="off"
                        onChange={(e) =>
                          setKid(e.target.value.replace('"', ""))
                        } // remove " that might be copied by mistake
                        disabled={guideMode}
                      />
                      <ScreenshotAccordion
                        images={[
                          {
                            file: okta_kid_copy,
                            altext: "okta kid example",
                            note: "NOTE: the values shown in the screenshot are for illustration purposes only",
                          },
                        ]}
                        onExpanded={addToExpandedList}
                      />
                    </GuidelineStep>
                    <GuidelineStep title="Copy the client ID">
                      <TextField
                        fullWidth
                        required
                        variant="filled"
                        name="clientId"
                        label="Okta Client ID"
                        value={clientId}
                        autoComplete="off"
                        onChange={(e) => setClientId(e.target.value)}
                        disabled={guideMode}
                      />
                      <ScreenshotAccordion
                        images={[
                          {
                            file: okta_client_id,
                            altext: "Client ID example",
                            note: "NOTE: the values shown in the screenshot are for illustration purposes only",
                          },
                        ]}
                        onExpanded={addToExpandedList}
                      />
                    </GuidelineStep>
                    <GuidelineStep title="Add Okta domain">
                      <GuidelineInstruction text="Locate the Okta domain by clicking your username in the upper-right corner of the Admin Console. The domain appears in the dropdown menu." />
                      <TextField
                        required
                        fullWidth
                        variant="filled"
                        label="Okta Domain"
                        name="customerDomain"
                        autoComplete="off"
                        value={customerDomain}
                        onChange={(e) => {
                          // remove https:// if pasted
                          let value = e.target.value;
                          if (value.startsWith("https://")) {
                            value = value.slice(8);
                          }
                          setCustomerDomain(value);
                        }}
                        disabled={guideMode}
                      />
                      <ScreenshotAccordion
                        images={[
                          {
                            file: okta_domain,
                            altext: "Okta domain example",
                          },
                        ]}
                        onExpanded={addToExpandedList}
                      />
                    </GuidelineStep>
                    <GuidelineStep title="Disable Demonstrating Proof of Possession (DPoP)">
                      <GuidelineInstruction text="Navigate to “General Settings” and disable Proof of Possession." />
                      <GuidelineInstruction text="Make sure its unchecked as shown in the screenshot below." />
                      <Typography variant="body2">
                        <strong>Note:</strong> DPoP is a security feature that
                        ensures that the client is in possession of the private
                        key associated with the client ID. This feature will be
                        introduced in the upcoming versions of the Breeze
                        platform.
                      </Typography>
                      <ScreenshotAccordion
                        images={[
                          {
                            file: okta_diable_dpop,
                            altext: "okta unchecked dpop example",
                          },
                        ]}
                        onExpanded={addToExpandedList}
                      />
                    </GuidelineStep>
                    <GuidelineStep title="Add Breeze IPs using the Network IP section">
                      <GuidelineInstruction
                        text={
                          <span>
                            Add the following IP addresses to the allowlist:{" "}
                            <strong>
                              {allowlistIps.map((ip, index) => (
                                <Box
                                  key={index}
                                  display="flex"
                                  alignItems="center"
                                  gap={1}
                                  sx={{
                                    margin: 0,
                                    padding: "4px 0",
                                  }}
                                >
                                  <span>{ip}</span>
                                  <CustomCopyButton
                                    copyTitle={"IP Address"}
                                    copyText={ip}
                                  />
                                </Box>
                              ))}
                            </strong>
                          </span>
                        }
                      />
                    </GuidelineStep>
                    <GuidelineStep title="Edit the rate limits for the app">
                      <GuidelineInstruction text="Navigate to the “Application Rate Limits” tab and then press “Edit” and move the slider to 80%." />
                      <GuidelineInstruction text="Click “Save” to save the new rate limit." />
                      <ScreenshotAccordion
                        images={[
                          {
                            file: okta_rate_limits,
                            altext: "Rate limits settings",
                          },
                        ]}
                        onExpanded={addToExpandedList}
                      />
                    </GuidelineStep>
                    <GuidelineStep title="Add read scopes to the app">
                      <GuidelineInstruction text="Navigate to the “Okta API Scopes” tab and click “Grant” for ALL read scopes. Note that you may reach a rate limit. If this happens, wait 10 seconds and try again." />{" "}
                      <Typography variant="body2">
                        <strong>Note:</strong> Scopes define what actions the
                        admin is allowed to do, such as managing users, looking
                        at apps, etc.
                      </Typography>
                      <ScreenshotAccordion
                        images={[
                          {
                            file: okta_scopes_grant,
                            altext: "Scope grant dialog",
                          },
                        ]}
                        onExpanded={addToExpandedList}
                      />
                    </GuidelineStep>
                    <GuidelineStep title="Add Role to the application">
                      <GuidelineInstruction text="Lastly, we need to assign a role to allow access to okta resources." />
                      <GuidelineInstruction text="Navigate to the “Admin roles“ tab and click “Edit assignments“ and add “Super Administrator“ " />
                      <Typography variant="body2">
                        <strong>Note:</strong> Admin roles specify what areas an
                        admin can access and work on, like certain groups of
                        users or specific apps. So, the admin roles must be
                        given enough rights to do the tasks outlined in the
                        scopes. This is not granting any permissions.
                      </Typography>
                      <ScreenshotAccordion
                        images={[
                          {
                            file: okta_role,
                            altext: "Role settings",
                          },
                        ]}
                        onExpanded={addToExpandedList}
                      />

                      <Typography variant="body2">
                        Click "Save Changes" to save the role assignment.
                      </Typography>
                      <Typography variant="body2">
                        <strong>Note:</strong> (optional) A notification message
                        might appear. If it does, please log out and log back in
                        to add the role to the application.
                      </Typography>
                      <ScreenshotAccordion
                        images={[
                          {
                            file: okta_optional_error,
                            altext: "Optional error message",
                          },
                        ]}
                        onExpanded={addToExpandedList}
                      />
                    </GuidelineStep>
                    <GuidelineStep title="All done, now hit “Connect“ to integrate!">
                      <Box display="flex" justifyContent="center">
                        <PrimaryButton type="submit" disabled={guideMode}>
                          Connect
                        </PrimaryButton>
                      </Box>
                    </GuidelineStep>
                  </GuidelineStepByStep>
                </form>
                <Box
                  sx={{
                    height: "4px",
                    backgroundColor: colors.primary_dark,
                    marginTop: "80px",
                    borderRadius: "2px",
                    width: "100%",
                  }}
                />
                <Typography variant="h4" mt="30px">
                  Remediation permissions (optional)
                </Typography>
                <Box mb="20px" mt="15px" minWidth="100%">
                  <Accordion
                    expanded={optionalExpanded}
                    onChange={handleOptionalAccordionChange}
                    sx={{
                      boxShadow: optionalExpanded
                        ? "none"
                        : "0px 2px 8px rgba(0, 0, 0, 0.15)",
                      "&:before": { display: "none" },
                      backgroundColor: optionalExpanded
                        ? "transparent"
                        : colors.grey_100,
                      overflowY: "auto",
                      borderRadius: optionalExpanded ? "4px 4px 0 0" : "4px",
                      border: optionalExpanded
                        ? `1px solid ${colors.grey_200}`
                        : "none",
                      borderBottom: optionalExpanded ? "none" : undefined,
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        backgroundColor: optionalExpanded
                          ? "transparent"
                          : colors.grey_200,
                      },
                    }}
                    TransitionProps={{ timeout: 150 }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="okta-remediation-content"
                      id="okta-remediation-header"
                      sx={{
                        padding: "12px 16px",
                        minHeight: "48px",
                        "& .MuiAccordionSummary-content": { margin: 0 },
                        cursor: "pointer",
                      }}
                    >
                      <Typography variant={"h6"}>
                        Instructions for configuring remediation permissions for
                        Okta integration.
                        <br />
                        Note: By granting Breeze remediation permissions, Breeze
                        can perform remediation actions on your behalf (e.g.,
                        disable password reveal).
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{
                        padding: "16px",
                        maxHeight: "60vh",
                        overflowY: "auto",
                        overflowX: "hidden",
                        backgroundColor: colors.white,
                        borderLeft: `1px solid ${colors.grey_200}`,
                        borderRight: `1px solid ${colors.grey_200}`,
                        borderBottom: `1px solid ${colors.grey_200}`,
                        borderRadius: "0 0 4px 4px",
                      }}
                    >
                      <Box mb="20px">
                        <Grid container spacing={2} direction={"row"}>
                          <Grid item>
                            <SecondaryButton onClick={handleExpandAll}>
                              Expand All Screenshots
                            </SecondaryButton>
                          </Grid>
                          <Grid item>
                            <SecondaryButton onClick={handleCollapseAll}>
                              Collapse All Screenshots
                            </SecondaryButton>
                          </Grid>
                        </Grid>
                      </Box>
                      <GuidelineStepByStep>
                        <GuidelineStep title="Add management scopes to the app">
                          <GuidelineInstruction text="Navigate to the “Okta API Scopes” tab and click “Grant” for the following management scopes. Note that you may reach a rate limit. If this happens, wait 10 seconds and try again." />{" "}
                          <Box
                            sx={{
                              width: "700px",
                              backgroundColor: colors.grey_200,
                              padding: "16px",
                              borderRadius: "8px",
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              color={colors.primary_darker}
                              gutterBottom
                            >
                              Management scopes
                            </Typography>
                            <Typography variant="body2" gutterBottom>
                              Check the box for each completed permission to
                              track the progress
                            </Typography>
                            {isLoadingManagementScopes ? (
                              <Box display="flex" alignItems="center" gap={1}>
                                <LoadingIcon size={20} />
                                <Typography variant="body2">
                                  Loading…
                                </Typography>
                              </Box>
                            ) : (
                              managementScopes?.map((role, index) => (
                                <Box
                                  key={index}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    marginBottom: "8px",
                                  }}
                                >
                                  <Checkbox />
                                  <Typography>{role}</Typography>
                                </Box>
                              ))
                            )}
                          </Box>
                          <ScreenshotAccordion
                            images={[
                              {
                                file: okta_management_scopes,
                                altext: "Management scopes settings",
                              },
                            ]}
                            onExpanded={addToExpandedList}
                          />
                        </GuidelineStep>
                      </GuidelineStepByStep>
                      <Box mt="30px">
                        <PrimaryButton
                          onClick={handleCheckManagementScopes}
                          disabled={isLoadingCheckOktaManagementScopes}
                        >
                          {isLoadingCheckOktaManagementScopes ? (
                            <LoadingIcon
                              size={24}
                              sx={{ color: colors.common_white }}
                            />
                          ) : (
                            "Check Management Scopes"
                          )}
                        </PrimaryButton>
                        {isLoadingCheckOktaManagementScopes && (
                          <Box mt={2}>
                            <Typography variant="body2">
                              Checking scopes...
                            </Typography>
                          </Box>
                        )}
                        {missingManagementScopes?.length > 0 && (
                          <Box mt={2}>
                            <Typography color="error" variant="subtitle1">
                              Please grant the missing scopes to the
                              application:
                            </Typography>
                            {missingManagementScopes.map((scope, index) => (
                              <Typography key={index} color="error">
                                <b>
                                  {index + 1}. {scope}
                                </b>
                              </Typography>
                            ))}
                          </Box>
                        )}
                        {missingManagementScopes?.length === 0 &&
                          !isLoadingCheckOktaManagementScopes && (
                            <Box mt={2}>
                              <Typography
                                color="success.main"
                                variant="subtitle1"
                              >
                                All required scopes are granted!
                              </Typography>
                            </Box>
                          )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Box>
              </Box>
            )
          }
        />
        <BreezeStaticIPsModal
          open={showStaticIPsModal}
          onClose={() => setShowStaticIPsModal(false)}
        />
      </Box>
    </div>
  );
};

export default OktaIntegration;
